/**
 * ProductsSetup Component
 *
 * Main component for product setup with form and modal
 */

import React, { useState } from 'react';
import { Button, Form, Modal, Empty } from 'antd';
import { PlusOutlined } from '@ant-design/icons';
import { ConfirmationModal } from '@/components/confirmationModal';
import { CardSkeleton } from '@/components/skeleton';
import { ProductForm } from './helper/form';
import { ProductCard } from './helper/card';
import { IProduct, IProductForm } from '../../types/productSetup';
import {
  useProductList,
  useCreateProduct,
  useUpdateProduct,
  useDeleteProduct,
  useUploadProductImage,
} from '../../hooks/productSetup';
import { APPENV } from '@/config';

export const ProductsSetup: React.FC = () => {
  const [form] = Form.useForm<IProductForm>();
  const [showEditor, setShowEditor] = useState(false);
  const [editingProduct, setEditingProduct] = useState<IProduct | null>(null);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [currentRecordId, setCurrentRecordId] = useState<string>();

  // Query params for pagination
  const [queryParams] = useState({
    current: 1,
    pageSize: 100,
    filter: '',
  });

  // API Hooks
  const { isLoading, data } = useProductList({ queryParams });
  const createProduct = useCreateProduct();
  const updateProduct = useUpdateProduct();
  const deleteProduct = useDeleteProduct();
  const uploadProductImage = useUploadProductImage();

  /**
   * Upload a single image file and return the URL from the response
   */
  const uploadSingleImage = async (productId: string, imageFile: any): Promise<string | null> => {
    const originFileObj = imageFile?.originFileObj;
    if (!originFileObj) return null;

    const contentType = originFileObj.type || 'image/png';
    const fileName = originFileObj.name || 'product-image.png';

    // Read as ArrayBuffer to preserve all original image properties (including PNG transparency)
    const imageBuffer: ArrayBuffer = await (originFileObj as Blob).arrayBuffer();

    const response = await uploadProductImage.mutateAsync({
      imageData: imageBuffer,
      productGuid: productId,
      fileName,
      contentType,
    });

    return response?.imageUrl || null;
  };

  /**
   * Handle all product image uploads and patch the product with returned URLs
   */
  const handleProductImageUpload = async (
    productId: string,
    productImage: any,
    productImageWithHorns: any,
    productImageWithoutHorns: any,
    isCreating: boolean,
  ) => {
    const hasAnyImage =
      (productImage && productImage.length > 0) ||
      (productImageWithHorns && productImageWithHorns.length > 0) ||
      (productImageWithoutHorns && productImageWithoutHorns.length > 0);

    if (!hasAnyImage) return null;

    try {
      const [mainUrl, withHornsUrl, withoutHornsUrl] = await Promise.all([
        productImage?.length > 0 ? uploadSingleImage(productId, productImage[0]) : Promise.resolve(null),
        productImageWithHorns?.length > 0 ? uploadSingleImage(productId, productImageWithHorns[0]) : Promise.resolve(null),
        productImageWithoutHorns?.length > 0 ? uploadSingleImage(productId, productImageWithoutHorns[0]) : Promise.resolve(null),
      ]);

      const payload: any = {};
      if (mainUrl) payload.dogma_productimageurl = mainUrl;
      if (withHornsUrl) payload.dogma_withhornsurl = withHornsUrl;
      if (withoutHornsUrl) payload.dogma_withouthornsurl = withoutHornsUrl;
      if (isCreating) payload.statecode = 0;

      if (Object.keys(payload).length > 0) {
        await updateProduct.mutateAsync({ id: productId, payload });
      }

      return { mainUrl, withHornsUrl, withoutHornsUrl };
    } catch (error) {
      throw error;
    }
  };

  const handleSave = async () => {
    try {
      const values = await form.validateFields();
      const { productImage, productImageWithHorns, productImageWithoutHorns, ...productData } = values;

      // Strip comments and compact the window JSON before saving
      if (productData.dogma_proudctwindowjson && productData.dogma_proudctwindowjson.trim() !== '') {
        const stripped = productData.dogma_proudctwindowjson
          .replace(/\/\/[^\n]*/g, '')
          .replace(/\/\*[\s\S]*?\*\//g, '');
        productData.dogma_proudctwindowjson = JSON.stringify(JSON.parse(stripped));
      } else {
        delete productData.dogma_proudctwindowjson;
      }

      let productId: string;
      const isCreating = !editingProduct;
      const hasAnyImage =
        (productImage && productImage.length > 0) ||
        (productImageWithHorns && productImageWithHorns.length > 0) ||
        (productImageWithoutHorns && productImageWithoutHorns.length > 0);

      if (editingProduct) {
        // Update existing product
        await updateProduct.mutateAsync({
          id: editingProduct.productid,
          payload: productData,
        });
        productId = editingProduct.productid;
      } else {
        // Create new product
        const res = await createProduct.mutateAsync({
          ...productData,
          "defaultuomscheduleid@odata.bind": `/uoms(${APPENV.DEFAULT_OMSCHEDULE_ID})`,
          "defaultuomid@odata.bind": `/uomschedules(${APPENV.UOMID})`,
          "statecode": 0,
        });
        productId = res.productid;
      }

      // Upload all images; if creating with images, activation is handled inside
      await handleProductImageUpload(productId, productImage, productImageWithHorns, productImageWithoutHorns, isCreating);

      // Activate product only if newly created AND no images were uploaded
      if (isCreating && !hasAnyImage) {
        await handleToggleActive(productId, 0);
      }

      setShowEditor(false);
      form.resetFields();
      setEditingProduct(null);
    } catch (error) {
    }
  };

  const handleEdit = (product: IProduct) => {
    setEditingProduct(product);

    // Pretty-print the stored JSON so it's readable in the editor
    let windowJson = product.dogma_proudctwindowjson || '';
    if (windowJson) {
      try {
        windowJson = JSON.stringify(JSON.parse(windowJson), null, 2);
      } catch {
        // leave as-is if it can't be parsed
      }
    }

    form.setFieldsValue({
      name: product.name,
      productnumber: product.productnumber,
      description: product.description,
      price: product.price,
      quantityonhand: product.quantityonhand,
      productstructure: product.productstructure,
      statuscode: product.statuscode,
      producttypecode: product?.producttypecode,
      dogma_proudctwindowjson: windowJson,
      dogma_numberofsash: product.dogma_numberofsash,
      dogma_unglazedprice: product.dogma_unglazedprice,
    });
    setShowEditor(true);
  };

  const handleDelete = async () => {
    if (currentRecordId) {
      await deleteProduct.mutateAsync(currentRecordId);
      setCurrentRecordId(undefined);
      setShowDeleteModal(false);
    }
  };

  const handleCancel = () => {
    setShowEditor(false);
    form.resetFields();
    setEditingProduct(null);
  };

  const handleAddNew = () => {
    setEditingProduct(null);
    form.resetFields();
    setShowEditor(true);
  };

  const handleToggleActive = async (productId: string, statuscode: number) => {
    await updateProduct.mutateAsync({
      id: productId,
      payload: { statecode: statuscode },
    });
  };

  return (
    <>
      <div className='bg-white rounded-lg'>
        <div className="flex h-full items-center px-4 py-4 border-b border-neutral-400 justify-between gap-x-4 border-0">
          <div>
            <h1 className='text-lg font-semibold text-text-primary'>Products (Window Types)</h1>
            <p className='text-sm text-text-secondary'>Configure all the products type</p>
          </div>
          <Button
            type="primary"
            icon={<PlusOutlined />}
            onClick={handleAddNew}
            className="shadow-none"
          >
            Add Product
          </Button>
        </div>

        <div className="p-4">
          {isLoading ? (
            <CardSkeleton count={6} />
          ) : data?.value && data.value.length > 0 ? (
            <div className="bg-white rounded-lg">
              <div className="grid grid-cols-2 gap-4">
                {data.value.map((product) => (
                  <ProductCard
                    key={product.productid}
                    product={product}
                    onUpdate={handleEdit}
                    onDelete={(productId) => {
                      setCurrentRecordId(productId);
                      setShowDeleteModal(true);
                    }}
                    onToggleActive={handleToggleActive}
                  />
                ))}
              </div>
            </div>
          ) : (
            <Empty description="No products added yet" />
          )}
        </div>
      </div>

      {showEditor && <Modal
        title={editingProduct ? 'Edit Product' : 'Add Product'}
        open={showEditor}
        onCancel={handleCancel}
        onOk={handleSave}
        okText="Save"
        cancelText="Cancel"
        width={800}
        confirmLoading={createProduct.isPending || updateProduct.isPending}
        styles={{
          body: {
            maxHeight: '60dvh',
            overflowY: 'auto',
            overflowX: 'hidden',
          },
        }}
      >
        <Form
          form={form}
          layout="vertical"
        >
          <ProductForm
            existingImageUrl={editingProduct?.dogma_productimageurl}
            existingWithHornsUrl={editingProduct?.dogma_withhornsurl}
            existingWithoutHornsUrl={editingProduct?.dogma_withouthornsurl}
          />
        </Form>
      </Modal>}

      <ConfirmationModal
        content="Are you sure you want to delete this product? This action cannot be undone."
        open={showDeleteModal}
        cancelBtnText="Cancel"
        okBtnText="Delete"
        loading={deleteProduct.isPending}
        onClose={() => {
          setCurrentRecordId(undefined);
          setShowDeleteModal(false);
        }}
        onConfirm={handleDelete}
      />
    </>
  );
};
